import { useCallback } from 'react';
import CameraOrbitControls from './CameraOrbitControls';
import {
  FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  formatFlythroughCameraAngleLabel,
  isValidFlythroughAngleStep,
  normalizeFlythroughCameraAngle,
  snapFlythroughCameraAngle,
} from '../utils/photorealistic/flythroughCameraAngles';
import { FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_MAX } from '../utils/photorealistic/flythroughVideoConfig';

const FlythroughCameraAngleSequence = ({
  sequence = [FLYTHROUGH_CANONICAL_CAMERA_ANGLE],
  onSequenceChange,
  draftAngle = FLYTHROUGH_CANONICAL_CAMERA_ANGLE,
  onDraftAngleChange,
}) => {
  const safeSequence = sequence.length
    ? sequence.map((angle) => snapFlythroughCameraAngle(angle))
    : [FLYTHROUGH_CANONICAL_CAMERA_ANGLE];
  const canAddMore = safeSequence.length < FLYTHROUGH_CAMERA_ANGLE_SEQUENCE_MAX;
  const lastAngle = safeSequence[safeSequence.length - 1];
  const draft = snapFlythroughCameraAngle(draftAngle);
  const draftMatchesLast =
    draft.azimuth === lastAngle.azimuth &&
    draft.elevation === lastAngle.elevation &&
    draft.distance === lastAngle.distance;
  const draftValid = isValidFlythroughAngleStep(lastAngle, draft);
  const showInvalidStepWarning = !draftMatchesLast && !draftValid;

  const handleAddAngle = () => {
    if (!canAddMore || !draftValid) return;
    onSequenceChange?.([...safeSequence, draft]);
    onDraftAngleChange?.(draft);
  };

  const handleRemoveAngle = (index) => {
    if (index <= 0 || safeSequence.length <= 1) return;
    const nextSequence = safeSequence.filter((_, itemIndex) => itemIndex !== index);
    onSequenceChange?.(nextSequence);
    onDraftAngleChange?.(nextSequence[nextSequence.length - 1]);
  };

  const handleSelectSequenceAngle = (index) => {
    const angle = safeSequence[index];
    if (!angle) return;
    onDraftAngleChange?.(angle);
  };

  const handleCameraChange = useCallback(
    (angle) => {
      const next = normalizeFlythroughCameraAngle(angle);
      onDraftAngleChange?.((prev) => {
        const current = snapFlythroughCameraAngle(prev);
        if (
          current.azimuth === next.azimuth &&
          current.elevation === next.elevation &&
          current.distance === next.distance
        ) {
          return prev;
        }
        return next;
      });
    },
    [onDraftAngleChange],
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-center text-[10px] text-gray-500">
        Original view is always first. Add up to 5 new angles (6 total). Each step may only
        change azimuth, elevation, or distance by one preset step.
      </p>

      <ol className="space-y-1 rounded-xl border border-gray-200 bg-white px-2 py-2 text-[10px] text-gray-600">
        {safeSequence.map((angle, index) => (
          <li
            key={`${angle.azimuth}-${angle.elevation}-${angle.distance}-${index}`}
            className="flex items-center gap-2"
          >
            <button
              type="button"
              onClick={() => handleSelectSequenceAngle(index)}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-0.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300/60"
              title="Show this angle in camera control"
              aria-label={`Show angle ${index + 1} in camera control`}
            >
              <span className="shrink-0 font-medium text-gray-500">{index + 1}.</span>
              <span className="min-w-0 flex-1">
                {index === 0 ? 'Original view · ' : ''}
                {formatFlythroughCameraAngleLabel(angle)}
              </span>
            </button>
            {index === safeSequence.length - 1 && index > 0 ? (
              <button
                type="button"
                onClick={() => handleRemoveAngle(index)}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:border-red-300 hover:text-red-600"
                title="Remove angle"
                aria-label={`Remove angle ${index + 1}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            ) : null}
          </li>
        ))}
      </ol>

      <CameraOrbitControls
        variant="render"
        cameraAngle={draft}
        onCameraChange={handleCameraChange}
      />

      {canAddMore ? (
        <>
          {showInvalidStepWarning ? (
            <p className="text-center text-[10px] text-amber-700">
              Change only one of azimuth, elevation, or distance by one step from the last angle.
            </p>
          ) : null}
          <button
            type="button"
            onClick={handleAddAngle}
            disabled={!draftValid}
            className="rounded-full border border-gray-800 bg-gray-800 px-3 py-2 text-[11px] font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add angle to sequence
          </button>
        </>
      ) : (
        <p className="text-center text-[10px] text-gray-500">Maximum camera angles reached.</p>
      )}
    </div>
  );
};

export default FlythroughCameraAngleSequence;
